/**
 * Cart context provider — shared cart state for CartItems + CartSummary blocks
 */

import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import type { AddBundleToCartRequest, Cart } from "@/types/cart";
import type { Result } from "@/types/layout";
import { linesNeedingVariant } from "@/types/cart";
import {
  fetchCart,
  updateCartLine,
  setCartLineVariant as apiSetCartLineVariant,
  addToCart as apiAddToCart,
  addBundleToCart as apiAddBundleToCart,
  addCoupon as apiAddCoupon,
  removeCoupon as apiRemoveCoupon,
} from "@/api/cart";
import { buildLoginUrl } from "@/utils/auth";

/**
 * Outcome of applying a coupon. On failure, `errorKey` is the backend's
 * validation translation key (e.g. `Tracker.Validation.Coupon.AlreadyUsed`),
 * or null when the request failed for another reason.
 */
export type ApplyCouponResult =
  | { ok: true }
  | { ok: false; errorKey: string | null };

export interface CartContextValue {
  cart: Cart | null;
  isLoading: boolean;
  isAddingToCart: boolean;
  isDrawerOpen: boolean;
  error: string | null;
  cartUrl: string | null;
  checkoutUrl: string | null;
  cartItemCount: number;
  addToCart: (
    productId: string,
    quantity?: number,
    variantId?: string
  ) => Promise<boolean>;
  /**
   * Adds a bundle, which lands in the cart as one line per included product.
   * Shares `isAddingToCart` with the ordinary add so a single button state
   * covers both.
   */
  addBundleToCart: (
    bundleProductId: string,
    quantity?: number,
    variants?: AddBundleToCartRequest["variants"]
  ) => Promise<boolean>;
  updateQuantity: (lineId: string, quantity: number) => Promise<void>;
  /**
   * Picks the version of a line already in the cart. Resolves to whether it stuck,
   * so the row can keep showing its picker on failure.
   */
  setLineVariant: (lineId: string, variantId: string) => Promise<boolean>;
  removeLine: (lineId: string) => Promise<void>;
  /**
   * Ids of the lines still owing a version, in cart order. Non-empty means
   * checkout is blocked.
   */
  unresolvedVariantLineIds: string[];
  /**
   * Asks the line list to scroll to and focus the first line still owing a
   * version. CartItems and CartSummary are sibling CMS blocks with no shared
   * state but this context, so the blocked checkout button reaches the offending
   * row through here.
   */
  requestVariantFocus: () => void;
  /**
   * Bumped by {@link requestVariantFocus}. A counter rather than a line id so a
   * second click on an already-focused row still re-scrolls to it.
   */
  variantFocusRequest: number;
  applyCoupon: (code: string) => Promise<ApplyCouponResult>;
  removeCoupon: (code: string) => Promise<boolean>;
  refreshCart: () => Promise<void>;
  clearCart: () => void;
  setDrawerOpen: (open: boolean) => void;
  toggleDrawer: () => void;
  clearError: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const CartContext = createContext<CartContextValue | null>(null);

interface CartProviderProps {
  children: ReactNode;
  cartUrl?: string | null;
  checkoutUrl?: string | null;
}

export function CartProvider({
  children,
  cartUrl,
  checkoutUrl,
}: CartProviderProps) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cartItemCount =
    cart?.lines.reduce((sum, line) => sum + line.quantity, 0) ?? 0;
  const abortRef = useRef<AbortController | null>(null);

  const loadCart = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    const result = await fetchCart(controller.signal);

    if (controller.signal.aborted) return;

    if (result.ok) {
      setCart(result.data);
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadCart();

    return () => {
      abortRef.current?.abort();
    };
  }, [loadCart]);

  // Both add paths (single product and bundle) share the same busy flag, cart
  // replacement, drawer-open and 401 handling — only the request differs.
  const runAdd = useCallback(
    async (request: () => Promise<Result<Cart>>): Promise<boolean> => {
      setIsAddingToCart(true);
      const result = await request();
      setIsAddingToCart(false);

      if (result.ok) {
        setCart(result.data);
        setIsDrawerOpen(true);
        return true;
      }

      // 401 from cart-add means the backend rejected a guest trying to add
      // a digital product. Redirect to identity server (no guest option) and
      // return the user to the current page so they can re-attempt the add.
      if (result.error.startsWith("401")) {
        const returnPath = window.location.pathname + window.location.search;
        window.location.href = buildLoginUrl({
          returnPath,
          allowGuest: false,
        });
        return false;
      }

      setError(result.error);
      return false;
    },
    []
  );

  const addToCartHandler = useCallback(
    (productId: string, quantity = 1, variantId?: string): Promise<boolean> =>
      runAdd(() => apiAddToCart(productId, quantity, variantId)),
    [runAdd]
  );

  const addBundleToCartHandler = useCallback(
    (
      bundleProductId: string,
      quantity = 1,
      variants?: AddBundleToCartRequest["variants"]
    ): Promise<boolean> =>
      runAdd(() => apiAddBundleToCart(bundleProductId, quantity, variants)),
    [runAdd]
  );

  const updateQuantity = useCallback(
    async (lineId: string, quantity: number) => {
      const result = await updateCartLine(lineId, quantity);
      if (result.ok) {
        setCart(result.data);
      } else {
        setError(result.error);
      }
    },
    []
  );

  const setLineVariant = useCallback(
    async (lineId: string, variantId: string): Promise<boolean> => {
      const result = await apiSetCartLineVariant(lineId, variantId);
      if (result.ok) {
        // The backend replaces the line, so the response carries a NEW line id for
        // it — the whole cart is swapped in rather than the row patched in place.
        setCart(result.data);
        return true;
      }
      setError(result.error);
      return false;
    },
    []
  );

  const removeLine = useCallback(
    async (lineId: string) => {
      // Setting quantity to 0 removes the line via the existing API
      await updateQuantity(lineId, 0);
    },
    [updateQuantity]
  );

  const [variantFocusRequest, setVariantFocusRequest] = useState(0);
  const requestVariantFocus = useCallback(
    () => setVariantFocusRequest((previous) => previous + 1),
    []
  );

  const unresolvedVariantLineIds = cart
    ? linesNeedingVariant(cart.lines).map((line) => line.id)
    : [];

  const applyCoupon = useCallback(
    async (code: string): Promise<ApplyCouponResult> => {
      const result = await apiAddCoupon(code);
      if (result.ok) {
        setCart(result.data);
        return { ok: true };
      }
      setError(result.error);
      return { ok: false, errorKey: result.validationErrors?.[0] ?? null };
    },
    []
  );

  const removeCouponHandler = useCallback(
    async (code: string): Promise<boolean> => {
      const result = await apiRemoveCoupon(code);
      if (result.ok) {
        setCart(result.data);
        return true;
      }
      setError(result.error);
      return false;
    },
    []
  );

  // Empties the cart locally after an order is placed — no network round-trip and
  // no isLoading flip (which would unmount the checkout/confirmation mid-flow).
  // Keeps the cart object non-null and its mode intact so the digital checkout
  // branch keeps rendering its confirmation view.
  const clearCart = useCallback(() => {
    setCart((prev) => (prev ? { ...prev, lines: [] } : prev));
  }, []);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => !prev);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return (
    <CartContext.Provider
      value={{
        cart,
        isLoading,
        isAddingToCart,
        isDrawerOpen,
        error,
        cartUrl: cartUrl ?? null,
        checkoutUrl: checkoutUrl ?? null,
        cartItemCount,
        addToCart: addToCartHandler,
        addBundleToCart: addBundleToCartHandler,
        updateQuantity,
        setLineVariant,
        removeLine,
        unresolvedVariantLineIds,
        requestVariantFocus,
        variantFocusRequest,
        applyCoupon,
        removeCoupon: removeCouponHandler,
        refreshCart: loadCart,
        clearCart,
        setDrawerOpen: setIsDrawerOpen,
        toggleDrawer,
        clearError,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}
